import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Sparkles, Loader2, RotateCcw } from "lucide-react";
import { aiUstozAPI } from "../../api";
import type { ChatMessage } from "./types";

const STORAGE_KEY = "ai_ustoz_conversation_id";
const MAX_LEN = 2000;

// Ko'p so'raladigan savollar — bo'sh chatda tugma sifatida chiqadi,
// bosilganda to'g'ridan-to'g'ri shu savol yuboriladi.
const SUGGESTED_QUESTIONS = [
  "1 haftada imtihonga qanday tayyorlansam bo'ladi?",
  "Prava olishim uchun nima qilishim kerak?",
  "Eng ko'p xato qiladigan mavzularim qaysilar?",
  "Nazorat (imtihon) testida qanday strategiya tutish kerak?",
];

interface LocalMessage extends ChatMessage {
  _key: string;
  failed?: boolean; // yuborishda xato — qayta yuborish mumkin
}

let keyCounter = 0;
const makeKey = () => `m_${Date.now()}_${keyCounter++}`;

const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const [conversationId, setConversationId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY),
  );
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [limitMsg, setLimitMsg] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // ─── Tarixni yuklash ────────────────────────────────────────────
  useEffect(() => {
    if (!conversationId) {
      setHistoryLoading(false);
      return;
    }
    aiUstozAPI
      .getConversation(conversationId)
      .then((res) =>
        setMessages(
          (res.data?.messages ?? []).map((m: ChatMessage) => ({
            ...m,
            _key: makeKey(),
          })),
        ),
      )
      .catch((e: any) => {
        if (e?.response?.status === 404) {
          localStorage.removeItem(STORAGE_KEY);
          setConversationId(null);
        }
      })
      .finally(() => setHistoryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, sending]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MAX_LEN || sending || limitReached) return;

    const key = makeKey();
    const userMsg: LocalMessage = {
      _key: key,
      role: "user",
      content: trimmed,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const res = await aiUstozAPI.sendMessage(trimmed, conversationId);
      const data = res.data;
      if (!conversationId) {
        localStorage.setItem(STORAGE_KEY, data.conversationId);
        setConversationId(data.conversationId);
      }
      setMessages((prev) => [
        ...prev,
        {
          _key: makeKey(),
          role: "assistant",
          content: data.reply,
          createdAt: new Date().toISOString(),
        },
      ]);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 429) {
        setLimitReached(true);
        setLimitMsg(
          e?.response?.data?.message ||
            "Kunlik AI so'rovlar limiti tugadi, ertaga qaytadan urinib ko'ring",
        );
        // yuborilmoqchi bo'lgan xabarni ro'yxatdan olib tashlaymiz — qayta yozib yuborish shart emas
        setMessages((prev) => prev.filter((m) => m._key !== key));
        setInput(trimmed);
      } else {
        // Xabarni saqlab qolamiz, lekin "yuborilmadi" belgisi bilan — qayta yuborish uchun
        setMessages((prev) =>
          prev.map((m) => (m._key === key ? { ...m, failed: true } : m)),
        );
      }
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const resetChat = () => {
    localStorage.removeItem(STORAGE_KEY);
    setConversationId(null);
    setMessages([]);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
      }}>
      {/* Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: "calc(52px + env(safe-area-inset-top))",
          paddingTop: "env(safe-area-inset-top)",
          flexShrink: 0,
        }}>
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate("/ai-ustoz")}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--muted)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}>
              <ArrowLeft size={16} /> Orqaga
            </button>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              Ustoz AI
            </span>
          </div>
          {messages.length > 0 && (
            <button
              onClick={resetChat}
              title="Yangi suhbat"
              style={{
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: 10,
                color: "var(--muted)",
                cursor: "pointer",
                padding: 7,
                display: "flex",
              }}>
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
        }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          {historyLoading ?
            <div
              style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <Loader2 className="animate-spin text-blue-500" size={26} />
            </div>
          : messages.length === 0 ?
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 12,
                paddingTop: 60,
              }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 18,
                  background: "rgba(59,130,246,.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <Sparkles size={24} style={{ color: "#3B82F6" }} />
              </div>
              <p style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                Assalomu alaykum, men Ustoz AI!
              </p>
              <p
                style={{
                  color: "var(--muted)",
                  fontSize: 13,
                  maxWidth: 280,
                  lineHeight: 1.5,
                }}>
                Yo'l harakati qoidalari bo'yicha savol bering — sizga yordam
                beraman.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  width: "100%",
                  maxWidth: 340,
                  marginTop: 8,
                }}>
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    disabled={sending}
                    style={{
                      textAlign: "left",
                      padding: "11px 14px",
                      borderRadius: 12,
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      color: "#E2E8F0",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: sending ? "not-allowed" : "pointer",
                      opacity: sending ? 0.5 : 1,
                    }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {messages.map((m) => (
                <div
                  key={m._key}
                  style={{
                    display: "flex",
                    justifyContent:
                      m.role === "user" ? "flex-end" : "flex-start",
                  }}>
                  <div
                    style={{
                      maxWidth: "80%",
                      padding: "10px 14px",
                      borderRadius:
                        m.role === "user" ?
                          "14px 14px 4px 14px"
                        : "14px 14px 14px 4px",
                      background:
                        m.role === "user" ? "#3B82F6" : "var(--card)",
                      border:
                        m.role === "user" ? "none" : "1px solid var(--border)",
                      color: m.role === "user" ? "#fff" : "#E2E8F0",
                      fontSize: 13,
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                    }}>
                    {m.content}
                    {m.failed && (
                      <button
                        onClick={() => {
                          setMessages((prev) =>
                            prev.filter((x) => x._key !== m._key),
                          );
                          send(m.content);
                        }}
                        style={{
                          display: "block",
                          marginTop: 8,
                          background: "transparent",
                          border: "none",
                          color: "#FCA5A5",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                          padding: 0,
                        }}>
                        ⚠️ Yuborilmadi — qayta yuborish
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {sending && (
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <div
                    style={{
                      padding: "10px 14px",
                      borderRadius: "14px 14px 14px 4px",
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}>
                    <span
                      className="animate-pulse"
                      style={{ color: "var(--muted)", fontSize: 13 }}>
                      Ustoz AI yozmoqda...
                    </span>
                  </div>
                </div>
              )}
            </div>
          }
        </div>
      </div>

      {/* Limit banner */}
      {limitReached && (
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto",
            width: "100%",
            padding: "0 16px 8px",
          }}>
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid rgba(239,68,68,.25)",
              borderRadius: 12,
              padding: "10px 14px",
              color: "#FCA5A5",
              fontSize: 12,
              fontWeight: 600,
            }}>
            {limitMsg}
          </div>
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        style={{
          borderTop: "1px solid var(--border)",
          background: "var(--bg)",
          padding: 12,
          flexShrink: 0,
        }}>
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto",
            display: "flex",
            alignItems: "flex-end",
            gap: 8,
          }}>
          <div style={{ flex: 1 }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, MAX_LEN))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              disabled={limitReached}
              placeholder="Savolingizni yozing..."
              rows={1}
              style={{
                width: "100%",
                resize: "none",
                padding: "12px 14px",
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 14,
                color: "#E2E8F0",
                fontSize: 13,
                fontFamily: "inherit",
                outline: "none",
                maxHeight: 120,
                opacity: limitReached ? 0.5 : 1,
              }}
            />
            {input.length > MAX_LEN - 200 && (
              <p
                style={{
                  color:
                    input.length >= MAX_LEN ? "#EF4444" : "var(--muted)",
                  fontSize: 10,
                  marginTop: 4,
                  textAlign: "right",
                }}>
                {input.length}/{MAX_LEN}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={!input.trim() || sending || limitReached}
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              border: "none",
              background:
                !input.trim() || sending || limitReached ?
                  "var(--card2)"
                : "#3B82F6",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor:
                !input.trim() || sending || limitReached ?
                  "not-allowed"
                : "pointer",
              flexShrink: 0,
            }}>
            <Send size={16} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatPage;
